import { Box, Typography } from '@mui/material';
import { DataGridPro, GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { GeneralConstants } from 'app/constants/general-constants';
import { ToplineMetric } from 'app/model/metrics/topline-metric';
import { RootState } from 'app/store';
import { RouteWithIdParam } from 'app/type/route-with-id-param';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

import { MetricsGraph } from './metrics-graph';
import { loadOverallMetrics, selectToplineMetric } from '../slice';

interface OverallMetricsProps extends RouteWithIdParam {
    // redux props
    metrics?: ToplineMetric[];

    // redux actions
    loadOverallMetrics: () => void;
}

interface OverallMetricsState {
    graphField?: string;
    graphLabel?: string;
}

class OverallMetrics extends React.Component<OverallMetricsProps, OverallMetricsState> {
    public constructor(props: OverallMetricsProps | Readonly<OverallMetricsProps>) {
        super(props);

        this.state = {};
    }

    public componentDidMount() {
        this.props.loadOverallMetrics();
    }

    public render(): React.ReactNode {
        const columns: Array<GridColDef<ToplineMetric>> = [
            {
                field: 'id',
                headerName: 'ID',
                width: 150,
            },
            {
                field: 'metricDate',
                headerName: 'Metric Month',
                width: 170,
                valueFormatter: DataGridFormatters.monthYearFormatter,
            },
            {
                field: 'activeCards',
                headerName: '# of Active Cards',
                width: 170,
                disableColumnMenu: true,
            },
            {
                field: 'activeMembers',
                headerName: '# of Individuals',
                width: 160,
                disableColumnMenu: true,
            },
            {
                field: 'totalSpend',
                headerName: 'Spend',
                width: 150,
            },
            {
                field: 'transactionCount',
                headerName: 'Transactions',
                width: 170,
            },
            {
                field: 'activeBanks',
                headerName: '# of Banks',
                width: 150,
            },
            {
                field: 'verifiedStores',
                headerName: '# of Midmatched Verified Stores',
                width: 270,
                disableColumnMenu: true,
            },
            {
                field: 'productionBrands',
                headerName: '# of Brands in Prod',
                width: 250,
            },
        ];

        return (
            <Box>
                <Typography variant='h4'>Overall Metrics</Typography>
                <Typography variant='body1'>
                    Click on a metric to see a graph. Click on a date to see breakout by stores.
                </Typography>
                {this.renderGraph()}
                <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                    <DataGridPro
                        columns={columns}
                        columnVisibilityModel={{
                            id: false,
                        }}
                        rows={this.props.metrics || []}
                        loading={undefined === this.props.metrics}
                        autoHeight
                        density='compact'
                        onCellClick={(params: GridCellParams<ToplineMetric>) => {
                            if ('metricDate' === params.field) {
                                this.props.history.push({
                                    pathname: '/seam/metrics/store',
                                    search: `date=${params.value as string}`,
                                });

                                return;
                            }

                            this.setState({
                                graphField: params.field,
                                graphLabel: (params.colDef as GridColDef).headerName,
                            });
                        }}
                    />
                </Box>
            </Box>
        );
    }

    private renderGraph() {
        if (!this.state.graphField) {
            return GeneralConstants.RENDER_NOTHING;
        }

        return (
            <MetricsGraph
                label={this.state.graphLabel || ''}
                data={this.props.metrics || []}
                x='metricDate'
                y={this.state.graphField}
            />
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    metrics: selectToplineMetric(state),
});

const thunks = { loadOverallMetrics };

const enhanced = withRouter(connect(mapStateToProps, thunks)(OverallMetrics));

export { enhanced as OverallMetrics };
